// stocai_frontend/src/components/HomePage/components/Journey/MyJourney.jsx

import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { UserContext } from "../../../../UserContext";
import JourneySequence from "./JourneySequence";
import { progressService } from "../../../../services/progressService";
import { apiFetch } from "../../../../services/apiFetch";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

// Subsection IDs for progressService
const FUTURE_IDENTITY_SUBSECTION_ID = "a6f1a312-7580-4988-8cc2-64d342be7ab3";
const CURRENT_IDENTITY_SUBSECTION_ID = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
const FUTURE_VALUES_SUBSECTION_ID = "f135631c-ac59-4ac1-8d68-7f522948d56f";
const VISUALIZATION_SUBSECTION_ID = "24089029-cf85-4073-a878-5697407f1515";

const MyJourney = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { layoutPhase, setLayoutPhase } = useOutletContext();

  const userId = user?.user_id;

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Journey data states
  const [selectedCard, setSelectedCard] = useState(null);
  const [futureData, setFutureData] = useState({});
  const [coreValues, setCoreValues] = useState([]);
  const [behaviours, setBehaviours] = useState([]);
  const [beliefs, setBeliefs] = useState([]);
  const [risks, setRisks] = useState([]);

  useEffect(() => {
    setLayoutPhase("full");
    return () => setLayoutPhase("intro");
  }, [setLayoutPhase]);

  useEffect(() => {
    if (!userId) {
      setError("User not found. Please log in.");
      setIsLoading(false);
      return;
    }

    const fetchJourneyData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch all data in parallel
        const [
          narrativeRes,
          futureIdentityRec,
          currentIdentityRec,
          futureValuesRec,
          vizRec,
        ] = await Promise.all([
          apiFetch(`${apiBaseURL}/users/${userId}/narrative`).catch(() => null),
          progressService.getUserProgress(userId, FUTURE_IDENTITY_SUBSECTION_ID).catch(() => null),
          progressService.getUserProgress(userId, CURRENT_IDENTITY_SUBSECTION_ID).catch(() => null),
          progressService.getUserProgress(userId, FUTURE_VALUES_SUBSECTION_ID).catch(() => null),
          progressService.getUserProgress(userId, VISUALIZATION_SUBSECTION_ID).catch(() => null),
        ]);

        // Parse narrative data
        if (narrativeRes?.ok) {
          const narrativeData = await narrativeRes.json();
          if (narrativeData?.name || narrativeData?.tagline) {
            setSelectedCard(narrativeData);
          } else {
            setSelectedCard(narrativeData);
          }
        } else {
          // Fallback: Try to find narrative in progress records
          try {
            const progressRes = await apiFetch(`${apiBaseURL}/users/${userId}/progress`);
            if (progressRes.ok) {
              const allProgress = await progressRes.json();
              const narrativeProgress = allProgress.find(p => {
                const payload = p.payload || p.data?.payload || {};
                return payload.selectedCard && (payload.selectedCard.name || payload.selectedCard.tagline);
              });

              if (narrativeProgress) {
                const payload = narrativeProgress.payload || narrativeProgress.data?.payload || {};
                setSelectedCard(payload.selectedCard);
              }
            }
          } catch (err) {
            console.error("Error fetching narrative:", err);
          }
        }

        // Parse future identity data
        const futurePayload = futureIdentityRec?.data?.payload || futureIdentityRec?.data || {};
        const currentPayload = currentIdentityRec?.data?.payload || currentIdentityRec?.data || {};

        const combinedFutureData = {
          userName: currentPayload.userName || "User",
          profileImageUrl: currentPayload.profileImage || "",
          designation: futurePayload.designation || futurePayload.futureTitle || currentPayload.currentTitle || "Your Role",
          salary: futurePayload.salary || currentPayload.salary || "",
          timeline: futurePayload.timeline || "",
          location: futurePayload.location || currentPayload.location || "",
          knownFor: Array.isArray(futurePayload.knownFor) ? futurePayload.knownFor : [],
          futureVoiceNote: currentPayload.futureVoiceNote || futurePayload.futureVoiceNote || "",
        };

        setFutureData(combinedFutureData);

        // Parse core values from future values subsection
        const valuesPayload = futureValuesRec?.data?.payload || futureValuesRec?.data || {};

        if (Array.isArray(valuesPayload.top3Values)) {
          const validValues = valuesPayload.top3Values.filter(Boolean);
          setCoreValues(validValues);
        }

        // Parse persona data from visualization subsection
        const vizPayload = vizRec?.data?.payload || vizRec?.data || {};

        const personaBehaviours = Array.isArray(vizPayload.behaviours) 
          ? vizPayload.behaviours 
          : [];
        const personaBeliefs = Array.isArray(vizPayload.beliefs) 
          ? vizPayload.beliefs 
          : [];
        const personaRisks = Array.isArray(vizPayload.risks) 
          ? vizPayload.risks 
          : [];

        setBehaviours(personaBehaviours);
        setBeliefs(personaBeliefs);
        setRisks(personaRisks);

        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching journey data:", err);
        setError("Failed to load journey data. Please try again.");
        setIsLoading(false);
      }
    };

    fetchJourneyData();
  }, [userId]);

  const handleJourneyComplete = () => {
    navigate("/learner/action-plan");
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 w-full h-screen flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4 animate-fadeIn">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#6FB1AF]"></div>
          <p className="text-gray-600 text-base font-medium font-gotham">Loading your journey...</p>
          <p className="text-gray-400 text-sm font-gotham">Please wait while we prepare your experience</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 w-full h-screen flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-6 max-w-md text-center px-6 animate-fadeIn">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 font-gotham mb-2">Oops! Something went wrong</h2>
            <p className="text-gray-600 font-gotham">{error}</p>
          </div>
          <button onClick={() => window.location.reload()} className="px-6 py-3 bg-[#6FB1AF] text-white rounded-lg font-medium font-gotham hover:bg-[#5FA09E] transition-colors">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 w-full h-screen bg-white z-40 flex justify-center">
        <JourneySequence
          userId={userId}
          onComplete={handleJourneyComplete}
          selectedCard={selectedCard}
          futureData={futureData}
          coreValues={coreValues}
          behaviours={behaviours}
          beliefs={beliefs}
          risks={risks}
          layoutPhase={layoutPhase}
        />
      </div>
    </>
  );
};

export default MyJourney;
